import { useState } from "react";
import { useQuery } from "convex/react";
import { Library, Plus } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ApplicationEnum } from "@repo/utils";
import {
  Breadcrumb,
  Button,
  PageContainer,
  PageHeader,
  type BreadcrumbItem,
} from "@repo/ui";
import {
  HistoriqueVersions,
  ListeScorecards,
  NouvelleScorecardDialog,
  ScorecardEditor,
  type LigneScorecard,
} from "@repo/scorecard-editor";
import { AssignerModeleDialog } from "./AssignerModeleDialog";
import { CreerModeleDialog } from "./CreerModeleDialog";
import { arianeOrganisation, libelleSection } from "../../lib/ariane";

// ============================================
// Deux racines : les modèles globaux (`/scorecards`) et les scorecards d'une
// organisation (`/organisations/:orgId/scorecards`). Éditeur et historique
// sont les mêmes composants ; seule la racine change le fil d'Ariane.
// ============================================

const RACINE_MODELES = "/scorecards";

/**
 * Marque d'URL d'un modèle tout juste créé depuis une scorecard d'org : l'éditeur
 * affiche alors l'avis de relecture tant que le modèle reste un brouillon.
 */
const ORIGINE_ORGANISATION = "organisation";
const racineOrg = (orgId: string) => `/organisations/${orgId}/scorecards`;

function useIds() {
  const { orgId, id } = useParams<{ orgId?: string; id?: string }>();
  return {
    organisationId: orgId as Id<"organisations"> | undefined,
    scorecardId: id as Id<"scorecards">,
  };
}

/** Fil d'Ariane jusqu'à la liste : modèles, ou organisation › scorecards. */
function useArianeListe(
  organisationId: Id<"organisations"> | undefined,
): BreadcrumbItem[] {
  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    organisationId ? { organisationId } : "skip",
  );
  if (!organisationId) {
    return [{ label: "Modèles de scorecards", href: RACINE_MODELES }];
  }
  return arianeOrganisation(organisationId, org?.nom, "scorecards");
}

export function ModelesScorecards() {
  const navigate = useNavigate();
  const [creation, setCreation] = useState(false);
  const lien = (id: Id<"scorecards">) => `${RACINE_MODELES}/${id}`;
  return (
    <PageContainer>
      <Breadcrumb
        items={[{ label: "Organisations", href: "/" }, { label: "Modèles de scorecards" }]}
      />
      <PageHeader
        titre="Modèles de scorecards"
        sousTitre="Catalogue Evaluans. Une organisation reçoit une copie indépendante de la version publiée : modifier un modèle ne change pas les copies déjà assignées."
        actions={
          <Button onClick={() => setCreation(true)}>
            <Plus /> Nouveau modèle
          </Button>
        }
      />
      {creation && (
        <NouvelleScorecardDialog
          cible={{ type: "modele" }}
          saisirSlug
          onFermer={() => setCreation(false)}
          onCreee={(id) => void navigate(lien(id))}
        />
      )}
      <ListeScorecards
        portee={{ type: "modeles" }}
        lienScorecard={lien}
        onOuvrir={(id) => void navigate(lien(id))}
        messageVide="Aucun modèle. Créez-en un, ou importez un JSON."
      />
    </PageContainer>
  );
}

export function ScorecardsOrganisation() {
  const navigate = useNavigate();
  const { organisationId } = useIds();
  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    organisationId ? { organisationId } : "skip",
  );
  const [assigner, setAssigner] = useState(false);
  const [creation, setCreation] = useState(false);
  if (!organisationId) return null;
  const lien = (id: Id<"scorecards">) => `${racineOrg(organisationId)}/${id}`;
  // Sans l'entretien structuré, les scorecards existantes restent lisibles
  // (support) mais rien ne s'y ajoute : le serveur refuse création et
  // assignation.
  const sansEntretien =
    org !== undefined &&
    org !== null &&
    !org.applicationsAutorisees.includes(ApplicationEnum.ENT_STRUCT);

  return (
    <PageContainer>
      <Breadcrumb items={arianeOrganisation(organisationId, org?.nom, "scorecards")} />
      <PageHeader
        titre={libelleSection("scorecards")}
        sousTitre={`Grilles d'entretien FairView de ${org?.nom ?? "l'organisation"}. Ses gestionnaires peuvent aussi les modifier depuis le portail client.`}
        actions={
          !sansEntretien && <>
            <Button variant="outline" onClick={() => setCreation(true)}>
              <Plus /> Nouvelle scorecard
            </Button>
            <Button onClick={() => setAssigner(true)}>
              <Plus /> Assigner un modèle
            </Button>
          </>
        }
      />
      {sansEntretien && (
        <p className="mb-4 rounded-surface border border-charte-line bg-muted p-4 text-sm">
          Activez FairView pour cette organisation
          pour lui créer ou lui assigner des scorecards, et en ouvrir la gestion
          à ses gestionnaires.{" "}
          <Link
            to={`/organisations/${organisationId}/modifier`}
            className="text-primary hover:underline"
          >
            Modifier l&apos;organisation
          </Link>
        </p>
      )}
      <ListeScorecards
        portee={{ type: "organisation", organisationId }}
        lienScorecard={lien}
        onOuvrir={(id) => void navigate(lien(id))}
        messageVide={
          sansEntretien
            ? "Aucune scorecard."
            : "Aucune scorecard : assignez un modèle à l'organisation, ou créez-en une."
        }
        actionsLigne={(ligne) => (
          <BoutonCreerModele source={ligne} variant="ghost" size="sm" />
        )}
      />
      {creation && (
        <NouvelleScorecardDialog
          cible={{ type: "organisation", organisationId }}
          saisirSlug
          onFermer={() => setCreation(false)}
          onCreee={(id) => void navigate(lien(id))}
        />
      )}
      {assigner && (
        <AssignerModeleDialog
          organisationId={organisationId}
          onFermer={() => setAssigner(false)}
          onAssigne={(id) => void navigate(lien(id))}
        />
      )}
    </PageContainer>
  );
}

/**
 * « Créer un modèle » à partir de la version publiée d'une scorecard d'org,
 * depuis la liste comme depuis l'éditeur.
 */
function BoutonCreerModele({
  source,
  variant,
  size,
}: {
  source: LigneScorecard;
  variant: "ghost" | "outline";
  size?: "sm";
}) {
  const navigate = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const publiee = source.numeroVersionPubliee !== null;
  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={!publiee}
        title={
          publiee
            ? "Créer un modèle du catalogue à partir de la version publiée"
            : "La scorecard doit être publiée pour en faire un modèle"
        }
        onClick={() => setOuvert(true)}
      >
        <Library /> Créer un modèle
      </Button>
      {ouvert && (
        <CreerModeleDialog
          source={source}
          onFermer={() => setOuvert(false)}
          onCree={(id) =>
            void navigate(`${RACINE_MODELES}/${id}?origine=${ORIGINE_ORGANISATION}`)
          }
        />
      )}
    </>
  );
}

/** Rappel affiché sur un modèle créé depuis une scorecard d'organisation. */
function AvisRelectureModele() {
  return (
    <p
      role="note"
      className="rounded-surface border border-etat-alerte-trait bg-etat-alerte-surface p-3 text-sm text-etat-alerte-contenu"
    >
      Modèle créé à partir de la scorecard d'une organisation : relisez le titre,
      la description et les questions pour retirer le nom du client et ce qui lui
      est propre. Il doit être <strong>publié</strong> pour pouvoir être assigné.
    </p>
  );
}

/** Éditeur, sous `/scorecards/:id` (modèle) ou `/organisations/:orgId/scorecards/:id`. */
export function ScorecardEditionAdmin() {
  const { organisationId, scorecardId } = useIds();
  const [params] = useSearchParams();
  const ariane = useArianeListe(organisationId);
  const racine = organisationId ? racineOrg(organisationId) : RACINE_MODELES;
  const depuisOrganisation = params.get("origine") === ORIGINE_ORGANISATION;
  return (
    <PageContainer>
      <Breadcrumb items={[...ariane, { label: "Modifier" }]} />
      <ScorecardEditor
        key={scorecardId}
        scorecardId={scorecardId}
        lienHistorique={`${racine}/${scorecardId}/historique`}
        avisBrouillon={depuisOrganisation && <AvisRelectureModele />}
        actions={
          organisationId &&
          ((scorecard) => <BoutonCreerModele source={scorecard} variant="outline" />)
        }
      />
    </PageContainer>
  );
}

export function ScorecardHistoriqueAdmin() {
  const { organisationId, scorecardId } = useIds();
  const ariane = useArianeListe(organisationId);
  const racine = organisationId ? racineOrg(organisationId) : RACINE_MODELES;
  return (
    <PageContainer>
      <Breadcrumb
        items={[
          ...ariane,
          { label: "Modifier", href: `${racine}/${scorecardId}` },
          { label: "Historique" },
        ]}
      />
      <HistoriqueVersions
        scorecardId={scorecardId}
        lienEditeur={`${racine}/${scorecardId}`}
      />
    </PageContainer>
  );
}
